import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DashboardOverviewDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { IconPlus, IconSearch } from '../components/Icons';
import { ErrorState, SchoolStatusChip, SchoolTile, SkeletonRows, SkeletonTiles, StatTile } from '../components/ui';
import { OnboardSchoolModal } from '../features/OnboardSchoolModal';
import { api, ApiRequestError } from '../lib/api';
import { formatBps, formatChange, formatINRCompact, formatLongDate, formatNumber, pluralise } from '../lib/format';

export function OverviewPage() {
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardOverviewDto>('/api/dashboard/overview'),
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        title="Platform overview"
        meta={`${data ? pluralise(data.schoolsLive.value, 'school') : '…'} live · ${formatLongDate()}`}
        actions={
          <>
            <button
              className="btn btn--ghost"
              onClick={() => navigate('/schools')}
              style={{ minWidth: 260, justifyContent: 'flex-start', background: 'var(--paper)', fontWeight: 500 }}
            >
              <IconSearch size={17} fill="var(--ink-soft)" />
              <span style={{ color: 'var(--ink-soft)' }}>Search schools, admins, tickets</span>
            </button>
            <button className="btn" onClick={() => setOnboarding(true)}>
              <IconPlus fill="var(--ink)" />
              Onboard school
            </button>
          </>
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load the dashboard'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <>
            <SkeletonTiles />
            <SkeletonRows />
          </>
        ) : (
          <>
            <div className="grid-4">
              <StatTile label="Schools live" value={formatNumber(data.schoolsLive.value)} caption={data.schoolsLive.caption} />
              <StatTile
                label="Students on platform"
                value={formatNumber(data.studentsOnPlatform.value)}
                caption={data.studentsOnPlatform.caption}
              />
              <StatTile
                label="MRR"
                value={formatINRCompact(data.mrrPaise.value)}
                caption={
                  formatChange(data.mrrPaise.changeBps)
                    ? `${formatChange(data.mrrPaise.changeBps)} ${data.mrrPaise.caption}`
                    : data.mrrPaise.caption
                }
                tone="success"
              />
              <StatTile
                label="Open tickets"
                value={formatNumber(data.openTickets.value)}
                caption={data.openTickets.caption}
                tone={data.openTickets.value > 0 ? 'danger' : undefined}
              />
            </div>

            <div className="grid-split">
              <div className="card" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div className="card__title">Schools</div>
                  <button
                    className="btn btn--ghost btn--sm"
                    style={{ marginLeft: 'auto', border: 'none', color: 'var(--marigold-deep)', fontWeight: 700 }}
                    onClick={() => navigate('/schools')}
                  >
                    Manage all
                  </button>
                </div>

                {data.schools.slice(0, 4).map((school) => (
                  <button
                    key={school.id}
                    onClick={() => navigate(`/schools/${school.id}`)}
                    style={{
                      background: 'var(--paper)',
                      border: '1px solid var(--rule)',
                      borderRadius: 16,
                      padding: 15,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    <SchoolTile initials={school.initials} primary={school.status === 'ACTIVE'} />
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 1 }}>
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{school.name}</span>
                      <span className="caption">
                        {formatNumber(school.studentCount)} students · {school.city}
                        {school.planName ? ` · plan: ${school.planName}` : ''}
                      </span>
                    </div>
                    <SchoolStatusChip status={school.status} trialDaysLeft={school.trialDaysLeft} />
                  </button>
                ))}
              </div>

              <div className="card card--dark" style={{ gap: 12 }}>
                <div className="card__title">Needs attention</div>
                {data.needsAttention.length === 0 ? (
                  <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.55)', padding: '11px 0' }}>
                    Nothing needs chasing right now.
                  </div>
                ) : (
                  data.needsAttention.map((item, index) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '11px 0',
                        borderBottom:
                          index === data.needsAttention.length - 1 ? 'none' : '1px solid rgba(255,255,255,.1)',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>{item.title}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>{item.detail}</div>
                      </div>
                      <button
                        className="chip"
                        style={{ background: 'var(--marigold)', color: 'var(--ink)', border: 'none', cursor: 'pointer' }}
                        onClick={() => navigate(item.href)}
                      >
                        {item.actionLabel}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card">
              <div className="card__title">System health · last 24h</div>
              <div className="grid-4">
                {data.serviceHealth.map((service) => (
                  <div key={service.service} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{service.label}</span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: service.state === 'OPERATIONAL' ? 'var(--leaf)' : service.state === 'DEGRADED' ? 'var(--marigold-deep)' : 'var(--coral)',
                      }}
                    >
                      {formatBps(service.successRateBps, 2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {onboarding ? <OnboardSchoolModal onClose={() => setOnboarding(false)} /> : null}
    </>
  );
}
